<template>
  <div class="traffic-assistant" :class="'traffic-assistant--' + activeMode.toLowerCase()">
    <!-- 页面头部 -->
    <header class="traffic-header">
      <div class="header-deco header-deco--1" />
      <div class="header-deco header-deco--2" />
      <div class="header-deco header-deco--3" />
      <div class="traffic-header-inner">
        <h1 class="traffic-title">{{ $t('trafficAssistant.title') }}</h1>
        <p class="traffic-subtitle">{{ $t('trafficAssistant.subtitle') }}</p>
      </div>
    </header>
  
    <div class="traffic-body">
      <section class="traffic-panel traffic-main-panel">
        <!-- 步骤指示 -->
        <div class="flow-steps">
          <span class="flow-step" :class="{ 'is-active': !loading && !hasResult, 'is-done': loading || hasResult }">{{ $t('trafficAssistant.input') }}</span>
          <span class="flow-step-line" :class="{ 'is-active': loading || hasResult }" />
          <span class="flow-step" :class="{ 'is-active': loading, 'is-done': hasResult && !loading }">{{ $t('trafficAssistant.query') }}</span>
          <span class="flow-step-line" :class="{ 'is-active': hasResult && !loading }" />
          <span class="flow-step" :class="{ 'is-active': hasResult && !loading }">{{ $t('trafficAssistant.result') }}</span>
        </div>

        <!-- 1. 输入 -->
        <div v-if="!loading && !hasResult" class="phase phase-input">
          <div class="mode-cards">
            <button
              v-for="mode in modeOptions"
              :key="mode.value"
              type="button"
              class="mode-card"
              :class="{ 'mode-card--active': activeMode === mode.value }"
              @click="switchMode(mode.value)"
            >
              <span class="mode-card-icon">
                <img :src="mode.icon" alt="模式" />
              </span>
              <span class="mode-card-name">{{ mode.label }}</span>
              <span class="mode-card-desc">{{ mode.desc }}</span>
            </button>
          </div>

          <!-- 铁路 / 航空 -->
          <div v-if="activeMode !== 'SUBWAY'" class="form-block">
            <div class="form-block-head">
              <div class="form-block-title-wrap">
                <i class="el-icon-map-location form-block-icon" />
                <span class="form-block-title">{{ $t('trafficAssistant.cityDistanceQuery') }}</span>
              </div>
              <div class="add-route-btn" @click="addRoute">
                <i class="el-icon-plus" />
                {{ $t('trafficAssistant.addRoute') }}
              </div>
            </div>

            <div class="route-list">
              <div
                v-for="(route, index) in routes"
                :key="index"
                class="route-row"
              >
                <span class="route-index">{{ index + 1 }}</span>
                <div class="route-inputs">
                  <el-autocomplete
                    v-model="route.from"
                    class="station-autocomplete"
                    :placeholder="$t('trafficAssistant.start')"
                    clearable
                    prefix-icon="el-icon-location-outline"
                    :fetch-suggestions="fetchRailAirSuggestions"
                    :trigger-on-focus="true"
                    popper-class="station-suggest-popper"
                  />
                  <el-autocomplete
                    v-model="route.to"
                    class="station-autocomplete"
                    :placeholder="$t('trafficAssistant.end')"
                    clearable
                    prefix-icon="el-icon-location"
                    :fetch-suggestions="fetchRailAirSuggestions"
                    :trigger-on-focus="true"
                    popper-class="station-suggest-popper"
                  />
                </div>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  class="route-delete"
                  :disabled="routes.length <= 1"
                  @click="removeRoute(index)"
                />
              </div>
            </div>

            <div class="quick-examples">
              <span class="quick-label">{{ $t('trafficAssistant.quickExamples') }}</span>
              <button
                v-for="(ex, i) in currentExamples"
                :key="i"
                type="button"
                class="quick-chip"
                @click="applyExample(ex)"
              >
                {{ ex.from }} → {{ ex.to }}
              </button>
            </div>

            <el-button
              type="primary"
              class="query-btn"
              icon="el-icon-search"
              @click="queryRailOrAir"
            >
              {{ $t('trafficAssistant.queryDistance') }}
            </el-button>
          </div>

          <!-- 地铁 -->
          <div v-else class="form-block">
            <div class="form-block-head">
              <div class="form-block-title-wrap">
                <i class="el-icon-place form-block-icon" />
                <span class="form-block-title">{{ $t('trafficAssistant.siteDistanceQuery') }}</span>
              </div>
            </div>

            <el-form label-width="72px" class="subway-form" @submit.native.prevent="querySubway">
              <el-form-item :label="$t('trafficAssistant.city')">
                <el-autocomplete
                  v-model="subwayForm.city"
                  class="station-autocomplete"
                  :placeholder="$t('trafficAssistant.cityPlaceholder')"
                  clearable
                  :fetch-suggestions="fetchMetroCitySuggestions"
                  :trigger-on-focus="true"
                  popper-class="station-suggest-popper"
                  @select="onMetroCitySelect"
                  @clear="onMetroCityClear"
                />
              </el-form-item>
              <el-form-item :label="$t('trafficAssistant.startStation')">
                <el-autocomplete
                  v-model="subwayForm.start"
                  class="station-autocomplete"
                  :placeholder="$t('trafficAssistant.startStationPlaceholder')"
                  clearable
                  :fetch-suggestions="fetchMetroStartSuggestions"
                  :trigger-on-focus="true"
                  popper-class="station-suggest-popper"
                />
              </el-form-item>
              <el-form-item :label="$t('trafficAssistant.endStation')">
                <el-autocomplete
                  v-model="subwayForm.ended"
                  class="station-autocomplete"
                  :placeholder="$t('trafficAssistant.endStationPlaceholder')"
                  clearable
                  :fetch-suggestions="fetchMetroEndSuggestions"
                  :trigger-on-focus="true"
                  popper-class="station-suggest-popper"
                />
              </el-form-item>
            </el-form>

            <div class="quick-examples">
              <span class="quick-label">{{ $t('trafficAssistant.quickExamples') }}</span>
              <button
                type="button"
                class="quick-chip"
                @click="applySubwayExample"
              >
                {{ $t('trafficAssistant.subwayExample') }}
              </button>
            </div>

            <el-button
              type="primary"
              class="query-btn"
              icon="el-icon-search"
              @click="querySubway"
            >
              {{ $t('trafficAssistant.querySubway') }}
            </el-button>
          </div>
        </div>

        <!-- 2. 加载动画 -->
        <div v-else-if="loading" class="phase phase-loading">
          <div class="loading-scene" :class="'loading-scene--' + activeMode.toLowerCase()">
            <template v-if="activeMode === 'RAIL'">
              <div class="scene-rail">
                <div class="scene-station scene-station--left">起</div>
                <div class="scene-track">
                  <div class="scene-track-rail scene-track-rail--top" />
                  <div class="scene-track-rail scene-track-rail--bottom" />
                  <div class="scene-track-sleeper" v-for="n in 8" :key="'sleeper-' + n" />
                  <div class="scene-vehicle scene-vehicle--rail">
                    <img :src="img_rail" alt="铁路" />
                  </div>
                </div>
                <div class="scene-station scene-station--right">终</div>
              </div>
            </template>

            <template v-else-if="activeMode === 'AIR'">
              <div class="scene-air">
                <div class="scene-cloud scene-cloud--1" />
                <div class="scene-cloud scene-cloud--2" />
                <div class="scene-cloud scene-cloud--3" />
                <div class="scene-vehicle scene-vehicle--air">
                  <img :src="img_air" alt="航空" />
                  <span class="scene-contrail" />
                </div>
              </div>
            </template>

            <template v-else>
              <div class="scene-subway">
                <div class="scene-metro-line" />
                <div
                  v-for="(dot, idx) in subwayLoadingDots"
                  :key="'metro-' + idx"
                  class="scene-metro-dot"
                  :class="{ 'is-active': idx <= subwayLoadingStep }"
                >
                  <span class="scene-metro-dot-inner" />
                  <span v-if="idx === 0" class="scene-metro-label">起</span>
                  <span v-if="idx === subwayLoadingDots - 1" class="scene-metro-label">终</span>
                </div>
                <div class="scene-vehicle scene-vehicle--subway">
                  <img :src="img_subway" alt="地铁" />
                </div>
              </div>
            </template>
          </div>
          <p class="loading-tip" :key="loadingTip">{{ loadingTip }}</p>
          <div class="loading-progress">
            <span class="loading-progress-bar" />
          </div>
        </div>

        <!-- 3. 输出结果 -->
        <div v-else class="phase phase-output">
          <div class="output-head">
            <span class="result-mode-tag" :class="'result-mode-tag--' + activeMode.toLowerCase()">
              <img :src="currentModeIcon" alt="" class="result-mode-icon" />
              {{ modeLabel }}里程
            </span>
          </div>

          <!-- 铁路 / 航空 -->
          <div v-if="activeMode !== 'SUBWAY' && railAirResult && railAirResult.results" class="output-list">
            <div
              v-for="item in railAirResult.results"
              :key="item.seq"
              class="output-item"
              :class="[
                'output-item--' + activeMode.toLowerCase(),
                { 'output-item--miss': item.status !== 'hit' }
              ]"
            >
              <div class="output-route">
                <span class="output-station">{{ item.station_a }}</span>
                <i class="el-icon-right output-arrow" />
                <span class="output-station">{{ item.station_b }}</span>
              </div>
              <div v-if="item.status === 'hit'" class="output-distance">
                <span class="output-distance-value">{{ item.mileage }}</span>
                <span class="output-distance-unit">公里</span>
              </div>
              <div v-else class="output-miss">{{ $t('trafficAssistant.outputItemMiss') }}</div>
              <!-- <p v-if="item.status === 'hit' && activeMode !== 'AIR'" class="output-meta">
                匹配：{{ item.matched_station_a }} → {{ item.matched_station_b }}
              </p> -->
            </div>
          </div>

          <!-- 地铁 -->
          <div v-else-if="activeMode === 'SUBWAY'" class="output-list">
            <div class="output-item output-item--subway">
              <div class="output-city">{{ subwayForm.city }}</div>
              <div class="output-route">
                <span class="output-station">{{ subwayForm.start }}</span>
                <i class="el-icon-right output-arrow" />
                <span class="output-station">{{ subwayForm.ended }}</span>
              </div>
              <div v-if="subwayDistance !== null" class="output-distance">
                <span class="output-distance-value">{{ (subwayDistance / 1000).toFixed(2) }}</span>
                <span class="output-distance-unit">公里</span>
              </div>
              <div v-else class="output-miss">{{ $t('trafficAssistant.outputItemMiss') }}</div>
            </div>
          </div>

          <el-button class="requery-btn" icon="el-icon-refresh" @click="resetQuery">
            {{ $t('trafficAssistant.requery') }}
          </el-button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getRailAndAirDistance, getSubwayDistance, getStationInfo } from '@/api/index';
import img_rail from '@/assets/images/carbonpath/TrafficAssistant_rail.svg';
import img_air from '@/assets/images/carbonpath/TrafficAssistant_air.svg';
import img_subway from '@/assets/images/carbonpath/TrafficAssistant_subway.svg';

const APP_HEADERS = { appKey: 'zhongTanDify' }; // 请求头
// 查询方式标签
const MODE_LABELS = {
  RAIL: '铁路',
  AIR: '航空',
  SUBWAY: '地铁',
};

// 查询方式选项
const MODE_OPTIONS = [
  { value: 'RAIL', label: '铁路', desc: '高铁/动车里程', icon: img_rail, gradient: 'linear-gradient(135deg, #7ae495, #339e9e)' },
  { value: 'AIR', label: '航空', desc: '航班航段距离', icon: img_air, gradient: 'linear-gradient(135deg, #60a5fa, #2563eb)' },
  { value: 'SUBWAY', label: '地铁', desc: '站点间距离', icon: img_subway, gradient: 'linear-gradient(135deg, #a78bfa, #6366f1)' },
];

// 铁路/航空快捷示例
const RAIL_EXAMPLES = [
  { from: '上海虹桥', to: '北京南' },
  { from: '北京南', to: '南通西' },
];

// 航空快捷示例
const AIR_EXAMPLES = [
  { from: '上海', to: '广州' },
  { from: '北京', to: '成都' },
];

// 加载动画提示
const LOADING_TIPS = {
  RAIL: ['正在调度高铁线路...', '匹配最近铁路站点...', '计算轨道运行里程...'],
  AIR: ['正在规划飞行航路...', '检索航班航段数据...', '测算两地航空距离...'],
  SUBWAY: ['正在扫描地铁线网...', '定位起终站点坐标...', '计算站内轨道距离...'],
};

// 加载动画最小持续时间
const LOADING_MIN_DURATION = {
  RAIL: 900,
  AIR: 1000,
  SUBWAY: 1200,
};

// 站点查询方式映射（铁路/航空/地铁）
const STATION_WAY_MAP = {
  RAIL: 'RAIL',
  AIR: 'AIR',
  SUBWAY: 'METRO',
};

// 站点查询建议数量限制
const SUGGEST_LIMIT = 50;

/** 前端模糊匹配站点列表 */
function filterStationNames(list, query, limit = SUGGEST_LIMIT) {
  if (!Array.isArray(list) || !list.length) return [];
  const q = (query || '').trim();
  if (!q) return [];
  const matched = list.filter((name) => name.includes(q));
  return matched.slice(0, limit).map((name) => ({ value: name }));
}

/** 模糊匹配地铁城市名 */
function filterMetroCities(metroMap, query, limit = SUGGEST_LIMIT) {
  if (!metroMap || typeof metroMap !== 'object') return [];
  const q = (query || '').trim();
  if (!q) return [];
  const cities = Object.keys(metroMap);
  const matched = cities.filter((city) => city.includes(q));
  return matched.slice(0, limit).map((city) => ({ value: city }));
}

export default {
  name: 'TrafficAssistant',
  data() {
    return {
      activeMode: 'RAIL',
      modeOptions: MODE_OPTIONS,
      routes: [{ from: '', to: '' }],
      subwayForm: {
        city: '',
        start: '',
        ended: '',
      },
      loading: false,
      hasResult: false,
      railAirResult: null,
      subwayDistance: null,
      loadingTipIndex: 0,
      loadingTipTimer: null,
      loadingStartedAt: 0,
      subwayLoadingStep: 0,
      subwayLoadingTimer: null,
      subwayLoadingDots: 6,
      stationCache: {
        RAIL: [],
        AIR: [],
        METRO: null,
      },
      stationsLoading: false,
      img_rail,
      img_air,
      img_subway,
    };
  },
  computed: {
    modeLabel() {
      return MODE_LABELS[this.activeMode] || '';
    },
    currentModeIcon() {
      const mode = MODE_OPTIONS.find((m) => m.value === this.activeMode);
      return mode ? mode.icon : 'el-icon-guide';
    },
    currentExamples() {
      return this.activeMode === 'AIR' ? AIR_EXAMPLES : RAIL_EXAMPLES;
    },
    loadingTip() {
      const tips = LOADING_TIPS[this.activeMode] || LOADING_TIPS.RAIL;
      return tips[this.loadingTipIndex % tips.length];
    },
  },
  beforeDestroy() {
    this.stopLoadingAnimation();
  },
  mounted() {
    this.loadStationsForMode(this.activeMode);
  },
  methods: {
    /** 获取站点查询方式 */
    getStationWay(mode) {
      return STATION_WAY_MAP[mode || this.activeMode] || 'RAIL';
    },
    /** 判断站点缓存是否准备好 */
    isStationCacheReady(mode) {
      const way = this.getStationWay(mode);
      if (way === 'METRO') return this.stationCache.METRO != null;
      return Array.isArray(this.stationCache[way]) && this.stationCache[way].length > 0;
    },
    /** 加载站点缓存 */
    async loadStationsForMode(mode) {
      const way = this.getStationWay(mode);
      if (this.isStationCacheReady(mode)) return;

      this.stationsLoading = true;
      try {
        const res = await getStationInfo(way);
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '站点数据加载失败');
          return;
        }
        if (way === 'METRO') {
          this.stationCache.METRO = res.data.data || {};
        } else {
          this.stationCache[way] = Array.isArray(res.data.data) ? res.data.data : [];
        }
      } catch (err) {
        this.$message.error(typeof err === 'string' ? err : '站点数据加载失败');
      } finally {
        this.stationsLoading = false;
      }
    },
    /** 获取当前站点列表 */
    getCurrentStationList() {
      const way = this.getStationWay(this.activeMode);
      if (way === 'METRO') return [];
      return this.stationCache[way] || [];
    },
    /** 获取铁路/航空站点查询建议 */
    fetchRailAirSuggestions(query, cb) {
      if (!this.isStationCacheReady(this.activeMode)) {
        this.loadStationsForMode(this.activeMode).then(() => {
          cb(filterStationNames(this.getCurrentStationList(), query));
        });
        return;
      }
      cb(filterStationNames(this.getCurrentStationList(), query));
    },
    /** 获取地铁城市查询建议 */
    fetchMetroCitySuggestions(query, cb) {
      const run = () => {
        cb(filterMetroCities(this.stationCache.METRO, query));
      };
      if (!this.isStationCacheReady('SUBWAY')) {
        this.loadStationsForMode('SUBWAY').then(run);
        return;
      }
      run();
    },
    /** 获取地铁站点列表 */
    getMetroStationsByCity(city) {
      const name = (city || '').trim();
      if (!name || !this.stationCache.METRO) return [];
      if (this.stationCache.METRO[name]) return this.stationCache.METRO[name];
      const matchedKey = Object.keys(this.stationCache.METRO).find(
        (key) => key.includes(name) || name.includes(key)
      );
      return matchedKey ? this.stationCache.METRO[matchedKey] : [];
    },
    /** 获取地铁起点站查询建议 */
    fetchMetroStartSuggestions(query, cb) {
      const run = () => {
        cb(filterStationNames(this.getMetroStationsByCity(this.subwayForm.city), query));
      };
      if (!this.isStationCacheReady('SUBWAY')) {
        this.loadStationsForMode('SUBWAY').then(run);
        return;
      }
      run();
    },
    /** 获取地铁终点站查询建议 */
    fetchMetroEndSuggestions(query, cb) {
      this.fetchMetroStartSuggestions(query, cb);
    },
    /** 地铁城市选择 */
    onMetroCitySelect(item) {
      this.subwayForm.city = item.value;
      this.subwayForm.start = '';
      this.subwayForm.ended = '';
    },
    /** 地铁城市清除 */
    onMetroCityClear() {
      this.subwayForm.start = '';
      this.subwayForm.ended = '';
    },
    /** 切换查询方式 */
    switchMode(mode) {
      if (this.activeMode === mode) return;
      this.activeMode = mode;
      this.routes.forEach((r) => {
        r.from = '';
        r.to = '';
      });
      this.subwayForm.city = '';
      this.subwayForm.start = '';
      this.subwayForm.ended = '';
      this.onModeChange();
      this.loadStationsForMode(mode);
    },
    /** 查询方式切换时重置状态 */
    onModeChange() {
      this.hasResult = false;
      this.railAirResult = null;
      this.subwayDistance = null;
    },
    /** 重置查询 */
    resetQuery() {
      this.hasResult = false;
      this.railAirResult = null;
      this.subwayDistance = null;
    },
    /** 开始加载动画 */
    startLoadingAnimation() {
      this.loadingStartedAt = Date.now();
      this.loadingTipIndex = 0;
      this.subwayLoadingStep = 0;
      this.stopLoadingAnimation();
      this.loadingTipTimer = setInterval(() => {
        this.loadingTipIndex += 1;
      }, 500);
      this.subwayLoadingTimer = setInterval(() => {
        this.subwayLoadingStep = (this.subwayLoadingStep + 1) % this.subwayLoadingDots;
      }, 250);
    },
    /** 确保最小加载时间 */
    ensureMinLoadingDuration() {
      const min = LOADING_MIN_DURATION[this.activeMode] || LOADING_MIN_DURATION.RAIL;
      const remaining = min - (Date.now() - this.loadingStartedAt);
      if (remaining <= 0) return Promise.resolve();
      return new Promise((resolve) => setTimeout(resolve, remaining));
    },
    /** 停止加载动画 */
    stopLoadingAnimation() {
      if (this.loadingTipTimer) {
        clearInterval(this.loadingTipTimer);
        this.loadingTipTimer = null;
      }
      if (this.subwayLoadingTimer) {
        clearInterval(this.subwayLoadingTimer);
        this.subwayLoadingTimer = null;
      }
    },
    /** 添加路线 */
    applyExample(ex) {
      this.routes = [{ from: ex.from, to: ex.to }];
    },
    /** 应用地铁快捷示例 */
    applySubwayExample() {
      this.subwayForm = { city: '上海', start: '后滩', ended: '人民广场' };
    },
    /** 添加路线 */
    addRoute() {
      this.routes.push({ from: '', to: '' });
    },
    /** 删除路线 */
    removeRoute(index) {
      if (this.routes.length <= 1) return;
      this.routes.splice(index, 1);
    },
    /** 验证路线 */
    validateRoutes() {
      const valid = this.routes.filter(
        (r) => r.from && r.from.trim() && r.to && r.to.trim()
      );
      if (!valid.length) {
        this.$message.warning('请至少填写一条完整的起点和终点');
        return null;
      }
      return valid.map((r) => ({
        from: r.from.trim(),
        to: r.to.trim(),
      }));
    },
    /** 查询铁路/航空 */
    async queryRailOrAir() {
      const queries = this.validateRoutes();
      if (!queries) return;

      this.loading = true;
      this.hasResult = false;
      this.startLoadingAnimation();
      try {
        const res = await getRailAndAirDistance(APP_HEADERS, {
          transport_mode: this.activeMode,
          queries,
        });
        await this.ensureMinLoadingDuration();
        this.railAirResult = res.data.data;
        this.hasResult = true;
      } catch (err) {
        await this.ensureMinLoadingDuration();
        this.$message.error(typeof err === 'string' ? err : '查询失败，请稍后重试');
      } finally {
        this.loading = false;
        this.stopLoadingAnimation();
      }
    },
    /** 查询地铁 */
    async querySubway() {
      const { city, start, ended } = this.subwayForm;
      if (!city.trim() || !start.trim() || !ended.trim()) {
        this.$message.warning('请填写城市、起点站和终点站');
        return;
      }

      this.loading = true;
      this.hasResult = false;
      this.startLoadingAnimation();
      let businessError = null;
      let distance = null;
      try {
        const res = await getSubwayDistance(APP_HEADERS, {
          city: city.trim(),
          start: start.trim(),
          ended: ended.trim(),
        });
        if (res.data.code !== 200) {
          if (res.data.code === 400) {
            distance = null;
          } else {
            businessError = res.data.data;
          }
        } else {
          distance = res.data.data;
        }
      } catch (err) {
        await this.ensureMinLoadingDuration();
        this.$message.error(typeof err === 'string' ? err : '查询失败，请稍后重试');
        this.loading = false;
        this.stopLoadingAnimation();
        return;
      }
      await this.ensureMinLoadingDuration();
      if (businessError) {
        this.$message.error(businessError);
      } else {
        this.subwayDistance = distance;
        this.hasResult = true;
      }
      this.loading = false;
      this.stopLoadingAnimation();
    },
  },
};
</script>

<style scoped lang="scss">
$rail: #339e9e;
$rail-light: #56c697;
$rail-lighter: #7ae495;
$air: #2563eb;
$air-light: #60a5fa;
$subway: #6366f1;
$subway-light: #a78bfa;
$text: #1a1a1a;
$text-secondary: #606266;

.traffic-assistant {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #eef1f6;

  &--air .traffic-header {
    background: linear-gradient(135deg, #93c5fd 0%, $air-light 40%, $air 100%);
  }

  &--subway .traffic-header {
    background: linear-gradient(135deg, #c4b5fd 0%, $subway-light 40%, $subway 100%);
  }
}

.traffic-header {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, $rail-lighter 0%, $rail-light 40%, $rail 100%);
}

.header-deco {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;

  &--1 { width: 200px; height: 200px; top: -80px; left: -40px; }
  &--2 { width: 120px; height: 120px; top: 10px; right: 8%; }
  &--3 { width: 80px; height: 80px; bottom: -20px; right: 25%; }
}

.traffic-header-inner {
  position: relative;
  padding: 20px 48px 18px 24px;
  text-align: center;
}

.traffic-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.traffic-subtitle {
  margin: 0 0 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.88);
}

.traffic-body {
  flex: 1;
  min-height: 0;
  padding: 16px;
  display: flex;
  justify-content: center;
}

.traffic-main-panel {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
}

.flow-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 20px;
  padding: 0 8px;
}

.flow-step {
  font-size: 12px;
  font-weight: 500;
  color: #c0c4cc;
  white-space: nowrap;
  transition: color 0.25s;

  &.is-active {
    color: $rail;
    font-weight: 600;
  }

  &.is-done {
    color: #909399;
  }
}

.traffic-assistant--air .flow-step.is-active { color: $air; }
.traffic-assistant--subway .flow-step.is-active { color: $subway; }

.flow-step-line {
  flex: 1;
  max-width: 48px;
  height: 2px;
  margin: 0 8px;
  background: #e4e7ed;
  border-radius: 1px;
  transition: background 0.25s;

  &.is-active {
    background: linear-gradient(90deg, $rail-light, $rail);
  }
}

.traffic-assistant--air .flow-step-line.is-active {
  background: linear-gradient(90deg, $air-light, $air);
}

.traffic-assistant--subway .flow-step-line.is-active {
  background: linear-gradient(90deg, $subway-light, $subway);
}

.phase {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.phase-loading {
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  min-height: 320px;
}

.phase-output {
  align-items: stretch;
  gap: 16px;
}

.traffic-panel {
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8eaed;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 20px;
  overflow-y: auto;
  // 去除滚动条
  &::-webkit-scrollbar {
    display: none;
  }
}

.mode-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.mode-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 6px;
  border: 2px solid #eef0f4;
  border-radius: 10px;
  background: #fafbfc;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    background: #fff;
  }

  &--active {
    border-color: $rail;
    background: linear-gradient(180deg, rgba($rail-lighter, 0.12) 0%, #fff 100%);
    box-shadow: 0 2px 10px rgba($rail, 0.12);

    .mode-card-name { color: $rail; }
  }
}

.traffic-assistant--air .mode-card--active {
  border-color: $air;
  background: linear-gradient(180deg, rgba($air-light, 0.12) 0%, #fff 100%);
  box-shadow: 0 2px 10px rgba($air, 0.12);
  .mode-card-name { color: $air; }
}

.traffic-assistant--subway .mode-card--active {
  border-color: $subway;
  background: linear-gradient(180deg, rgba($subway-light, 0.12) 0%, #fff 100%);
  box-shadow: 0 2px 10px rgba($subway, 0.12);
  .mode-card-name { color: $subway; }
}

.mode-card-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

.mode-card-name {
  font-size: 13px;
  font-weight: 600;
  color: $text;
}

.mode-card-desc {
  font-size: 11px;
  color: #909399;
}

.form-block-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.form-block-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-block-icon {
  font-size: 16px;
  color: $rail;
}

.traffic-assistant--air .form-block-icon { color: $air; }
.traffic-assistant--subway .form-block-icon { color: $subway; }

.form-block-title {
  font-size: 14px;
  font-weight: 600;
  color: $text;
}

.add-route-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $rail;
  cursor: pointer;

  &:hover {
    color: $rail;
  }
}

.traffic-assistant--air .add-route-btn { color: $air; }

.route-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.route-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  background: #f8fafb;
  border: 1px solid #eef1f5;
}

.route-index {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: linear-gradient(135deg, $rail-lighter, $rail);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
}

.traffic-assistant--air .route-index {
  background: linear-gradient(135deg, $air-light, $air);
}

.route-inputs {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 6px;

  .station-autocomplete {
    flex: 1;
    min-width: 0;
  }
}

.station-autocomplete {
  width: 100%;
}

.route-delete {
  flex-shrink: 0;
  color: #f56c6c;
  padding: 0;
  margin-top: 6px;

  &.is-disabled { color: #c0c4cc; }
}

.quick-examples {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 8px;
  background: #f5f7fa;
}

.quick-label {
  font-size: 12px;
  color: #909399;
}

.quick-chip {
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #dce3ed;
  background: #fff;
  font-size: 12px;
  color: $text-secondary;
  cursor: pointer;

  &:hover {
    border-color: $rail;
    color: $rail;
  }
}

.traffic-assistant--air .quick-chip:hover {
  border-color: $air;
  color: $air;
}

.traffic-assistant--subway .quick-chip:hover {
  border-color: $subway;
  color: $subway;
}

.subway-form {
  margin-bottom: 8px;

  ::v-deep .el-form-item { margin-bottom: 14px; }
  ::v-deep .el-form-item__label { color: $text-secondary; }

  .station-autocomplete {
    width: 100%;
  }
}

.query-btn {
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, $rail-light, $rail);

  &:hover, &:focus {
    background: linear-gradient(135deg, lighten($rail-light, 4%), lighten($rail, 4%));
  }
}

.traffic-assistant--air .query-btn {
  background: linear-gradient(135deg, $air-light, $air);
  &:hover, &:focus {
    background: linear-gradient(135deg, lighten($air-light, 4%), lighten($air, 4%));
  }
}

.traffic-assistant--subway .query-btn {
  background: linear-gradient(135deg, $subway-light, $subway);
  &:hover, &:focus {
    background: linear-gradient(135deg, lighten($subway-light, 4%), lighten($subway, 4%));
  }
}

.result-mode-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 14px;
  font-weight: 500;

  &--rail { background: rgba($rail, 0.1); color: $rail; border: 1px solid rgba($rail, 0.2); }
  &--air { background: rgba($air, 0.1); color: $air; border: 1px solid rgba($air, 0.2); }
  &--subway { background: rgba($subway, 0.1); color: $subway; border: 1px solid rgba($subway, 0.2); }
}

.result-mode-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.output-head {
  text-align: center;
  margin-bottom: 8px;
}

.output-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  // 去除滚动条
  &::-webkit-scrollbar {
    display: none;
  }
}

.output-item {
  padding: 16px;
  border-radius: 12px;
  background: #f8fafb;
  border: 1px solid #eef1f5;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  &--rail { border-left: 4px solid $rail; }
  &--air { border-left: 4px solid $air; }
  &--subway { border-left: 4px solid $subway; }
  &--miss { border-left-color: #e6a23c; background: #fdf6ec; }
}

.output-route {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.output-station {
  font-size: 15px;
  font-weight: 600;
  color: $text;
}

.output-city {
  max-width: 200px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: $subway;
  color: #fff;
  margin-bottom: 10px;
  text-align: center;
}

.output-arrow {
  color: #c0c4cc;
  font-size: 14px;
}

.output-distance {
  margin-bottom: 4px;
}

.output-distance-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
  color: $rail;
}

.output-item--air .output-distance-value { color: $air; }
.output-item--subway .output-distance-value { color: $subway; }

.output-distance-unit {
  font-size: 14px;
  color: #909399;
  margin-left: 4px;
}

.output-meta {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
}

.output-miss {
  font-size: 14px;
  color: #e6a23c;
}

.requery-btn {
  width: 100%;
  margin-top: auto;
  border-color: #dcdfe6;
  color: $text-secondary;

  &:hover {
    color: $rail;
    border-color: $rail;
    background: rgba($rail, 0.04);
  }
}

.traffic-assistant--air .requery-btn:hover {
  color: $air;
  border-color: $air;
  background: rgba($air, 0.04);
}

.traffic-assistant--subway .requery-btn:hover {
  color: $subway;
  border-color: $subway;
  background: rgba($subway, 0.04);
}

.query-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.loading-scene {
  width: 100%;
  max-width: 320px;
  height: 120px;
  margin-bottom: 20px;
  position: relative;
}

.loading-tip {
  margin: 0 0 14px;
  font-size: 14px;
  color: $text-secondary;
  animation: tip-fade 1.4s ease-in-out infinite;
}

.loading-progress {
  width: 160px;
  height: 4px;
  border-radius: 4px;
  background: #eef1f5;
  overflow: hidden;
}

.loading-progress-bar {
  display: block;
  height: 100%;
  width: 40%;
  border-radius: 4px;
  background: linear-gradient(90deg, $rail-light, $rail);
  animation: progress-slide 1.2s ease-in-out infinite;
}

.loading-scene--air .loading-progress-bar {
  background: linear-gradient(90deg, $air-light, $air);
}

.loading-scene--subway .loading-progress-bar {
  background: linear-gradient(90deg, $subway-light, $subway);
}

/* ── 铁路动画 ── */
.scene-rail {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
}

.scene-station {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: $rail;
  box-shadow: 0 2px 8px rgba($rail, 0.35);
  z-index: 2;
}

.scene-track {
  flex: 1;
  position: relative;
  height: 48px;
}

.scene-track-rail {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: #b0bec5;
  border-radius: 2px;

  &--top { top: 14px; }
  &--bottom { bottom: 14px; }
}

.scene-track-sleeper {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 2px;
  background: #cfd8dc;
  border-radius: 1px;

  @for $i from 1 through 8 {
    &:nth-child(#{$i + 2}) {
      left: percentage($i / 9);
    }
  }
}

.scene-vehicle--rail {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  // background: linear-gradient(135deg, $rail-lighter, $rail);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  // box-shadow: 0 4px 12px rgba($rail, 0.4);
  animation: rail-move 1.8s ease-in-out infinite;
  z-index: 3;
}

/* ── 航空动画 ── */
.scene-air {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: linear-gradient(180deg, #dbeafe 0%, #eff6ff 100%);
}

.scene-cloud {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 20px;
  animation: cloud-drift 3s linear infinite;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: inherit;
    border-radius: 50%;
  }

  &--1 {
    width: 40px;
    height: 14px;
    top: 24px;
    left: -50px;
    animation-duration: 2.8s;

    &::before { width: 18px; height: 18px; top: -8px; left: 6px; }
    &::after { width: 14px; height: 14px; top: -5px; left: 20px; }
  }

  &--2 {
    width: 32px;
    height: 12px;
    top: 60px;
    left: -40px;
    animation-duration: 3.5s;
    animation-delay: 0.6s;
    opacity: 0.7;

    &::before { width: 14px; height: 14px; top: -6px; left: 4px; }
    &::after { width: 12px; height: 12px; top: -4px; left: 16px; }
  }

  &--3 {
    width: 28px;
    height: 10px;
    top: 42px;
    left: -35px;
    animation-duration: 2.2s;
    animation-delay: 1.2s;
    opacity: 0.5;

    &::before { width: 12px; height: 12px; top: -5px; left: 3px; }
  }
}

.scene-vehicle--air {
  position: absolute;
  top: 50%;
  left: 10%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  animation: air-fly 1.5s ease-in-out infinite;
  z-index: 2;

  i {
    font-size: 28px;
    color: $air;
    transform: rotate(-15deg);
    filter: drop-shadow(0 2px 4px rgba($air, 0.3));
  }
}

.scene-contrail {
  position: absolute;
  right: 100%;
  top: 50%;
  width: 48px;
  height: 2px;
  margin-top: -1px;
  background: linear-gradient(90deg, transparent, rgba($air-light, 0.6));
  border-radius: 2px;
  animation: contrail-pulse 1s ease-in-out infinite;
}

/* ── 地铁动画 ── */
.scene-subway {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 8px;
}

.scene-metro-line {
  position: absolute;
  left: 24px;
  right: 24px;
  top: 50%;
  height: 4px;
  margin-top: -2px;
  background: #e8e0fc;
  border-radius: 4px;
  z-index: 0;
}

.scene-metro-dot {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  .scene-metro-dot-inner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ddd6fe;
    border: 2px solid #fff;
    transition: all 0.3s;
  }

  &.is-active .scene-metro-dot-inner {
    background: $subway;
    box-shadow: 0 0 0 4px rgba($subway, 0.2);
    transform: scale(1.15);
  }
}

.scene-metro-label {
  position: absolute;
  bottom: -18px;
  font-size: 10px;
  font-weight: 600;
  color: $subway;
  white-space: nowrap;
}

.scene-vehicle--subway {
  position: absolute;
  top: calc(50% - 22px);
  left: 24px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  // background: linear-gradient(135deg, $subway-light, $subway);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  // box-shadow: 0 3px 10px rgba($subway, 0.35);
  animation: subway-move 1.2s ease-in-out infinite;
  z-index: 2;
}

@keyframes rail-move {
  0% { left: 0; }
  50% { left: 100%; }
  100% { left: 0; }
}

@keyframes air-fly {
  0% { left: 5%; transform: translateY(-50%) rotate(-5deg); }
  90% { left: 75%; transform: translateY(calc(-50% - 8px)) rotate(5deg); }
  100% { left: 75%; transform: translateY(calc(-50% - 8px)) rotate(5deg); }
}

@keyframes cloud-drift {
  from { transform: translateX(calc(100% + 360px)); }
  to { transform: translateX(calc(-100% - 360px)); }
}

@keyframes contrail-pulse {
  0%, 100% { opacity: 0.3; width: 32px; }
  50% { opacity: 1; width: 56px; }
}

@keyframes subway-move {
  0% { left: 24px; }
  100% { left: calc(100% - 52px); }
}

@keyframes tip-fade {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes progress-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}
</style>
